import { z } from 'zod';

import { currentUserSchema, emailSchema } from './identity';

/**
 * Anmeldung, Auffrischung und zweiter Faktor.
 *
 * Kein einziges Token steht in diesen Schemas. Zugriffs- und
 * Auffrischungstoken reisen ausschließlich als `httpOnly`-Cookie
 * (Entscheidung B5); ein Feld dafür in der Antwort wäre die Einladung, es
 * doch im `localStorage` abzulegen.
 */

/**
 * Ein Passwort, wie es gesetzt werden darf.
 *
 * Mindestens zwölf Zeichen, **keine** Zeichenklassen-Pflicht (Entscheidung B3).
 * Die Begründung ist bewusst kein Geschmacksurteil: Zeichenklassen-Regeln
 * erzeugen `Sommer2026!` und verhindern `pferd korrekt batterie klammer`. Die
 * eigentliche Prüfung ist der Abgleich gegen bekannte Passwörter, und der
 * gehört nicht in ein Schema, sondern in die Domänenschicht des Masters —
 * er braucht eine Liste.
 *
 * Die Obergrenze ist kein Qualitätsmerkmal, sondern ein Riegel: argon2id
 * rechnet über die volle Länge, und ein Megabyte als „Passwort" wäre ein
 * kostenloser Weg, den Master zu beschäftigen.
 */
export const passwordSchema = z
  .string()
  .min(12, { message: 'Das Passwort muss mindestens 12 Zeichen lang sein.' })
  .max(200, { message: 'Das Passwort darf höchstens 200 Zeichen lang sein.' });

/** Ein Code aus der Authenticator-App: sechs Ziffern. */
export const totpCodeSchema = z
  .string()
  .regex(/^\d{6}$/, { message: 'Der Code besteht aus genau sechs Ziffern.' });

export const loginRequestSchema = z.object({
  email: emailSchema,
  /**
   * Bewusst nicht gegen `passwordSchema` geprüft.
   *
   * Bei der Anmeldung ist jedes Passwort zulässig — auch ein zu kurzes aus der
   * Zeit vor einer Regeländerung. Eine Validierung hier verriete außerdem die
   * Regeln, bevor jemand angemeldet ist, und träfe die Unterscheidung zwischen
   * „falsch" und „ungültig geformt" an einer Stelle, an der es genau eine
   * Antwort geben darf.
   */
  password: z.string().min(1, { message: 'Bitte das Passwort eingeben.' }),
  /** Nur nötig, wenn für das Konto ein zweiter Faktor eingerichtet ist. */
  totpCode: totpCodeSchema.optional(),
  /**
   * „Angemeldet bleiben" (Entscheidung B6).
   *
   * Wahr: Das Auffrischungs-Cookie bekommt ein Ablaufdatum von 30 Tagen.
   * Falsch: Es ist ein Sitzungs-Cookie und verschwindet mit dem Browserfenster.
   * Die Sitzung in der Datenbank läuft in beiden Fällen nach derselben Frist
   * ab — der Unterschied liegt allein im Browser, und deshalb ist er kein
   * Sicherheitsversprechen.
   */
  rememberMe: z.boolean().default(false),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

/**
 * Die Antwort auf einen Anmeldeversuch.
 *
 * Eine unterschiedene Union statt eines Wahrheitswerts: „angemeldet" und
 * „zweiter Faktor fehlt" sind zwei verschiedene Zustände, und nur im ersten
 * gibt es ein Konto zu zeigen. Mit einem optionalen Feld müsste die Oberfläche
 * raten, welcher Fall vorliegt.
 *
 * `totpRequired` ist ausdrücklich **kein** Fehler: Der erste Faktor stimmte.
 * Als 401 wäre er von einem falschen Passwort nicht zu unterscheiden.
 */
export const loginResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('authenticated'),
    user: currentUserSchema,
  }),
  z.object({
    status: z.literal('totp-required'),
  }),
]);

export type LoginResponse = z.infer<typeof loginResponseSchema>;

/** Antwort auf `POST /auth/refresh` — dieselbe Sicht wie nach der Anmeldung. */
export const refreshResponseSchema = z.object({
  user: currentUserSchema,
});

export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

/**
 * Der erste Schritt der TOTP-Einrichtung.
 *
 * Das Geheimnis wird ausgegeben, ist aber noch **nicht** scharf: Erst
 * `POST /auth/totp/confirm` mit einem gültigen Code schaltet es ein. Ohne
 * diese zwei Schritte sperrte sich aus, wer den QR-Code falsch scannt.
 */
export const totpEnrollResponseSchema = z.object({
  /** Das Geheimnis in Base32 — für die Eingabe von Hand. */
  secret: z.string().min(16),
  /** Dieselbe Angabe als `otpauth://`-URL für den QR-Code. */
  otpauthUrl: z.string().startsWith('otpauth://'),
});

export type TotpEnrollResponse = z.infer<typeof totpEnrollResponseSchema>;

export const totpConfirmRequestSchema = z.object({
  code: totpCodeSchema,
});

export type TotpConfirmRequest = z.infer<typeof totpConfirmRequestSchema>;

/**
 * Das Abschalten verlangt das Passwort, nicht den Code.
 *
 * Wer ein offenes Fenster vor sich hat, hat den Code — er liegt im Telefon
 * daneben. Das Passwort ist der Faktor, den ein fremder Zugriff auf eine offene
 * Sitzung nicht mitbringt.
 */
export const totpDisableRequestSchema = z.object({
  password: z.string().min(1, { message: 'Bitte das Passwort eingeben.' }),
});

export type TotpDisableRequest = z.infer<typeof totpDisableRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, { message: 'Bitte das aktuelle Passwort eingeben.' }),
  newPassword: passwordSchema,
});

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;
